import { DatePipe } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Patient } from '@pixacare/domain';
import { capitalize, getSectionListData, SectionConfig, SectionListData } from '@pixacare/shared';
import { SortOrderStrategy } from '@shared/models/enums/sort-order-strategy.enum';
import { isSameMonth, isThisMonth, isThisWeek, isToday, isYesterday, subMonths } from 'date-fns';

@Injectable()
export class PatientSectionListService {
  private readonly translocoService = inject(TranslocoService);
  private readonly datePipe = inject(DatePipe);

  private readonly ALPHABETICAL_REGEX = /^[A-Za-z]$/i;

  private readonly dateSectionConfigs: SectionConfig<Patient>[] = [
    {
      label: this.translocoService.translate("Aujourd'hui"),
      condition: (item) => isToday(item.lastActivity),
    },
    {
      label: this.translocoService.translate('Hier'),
      condition: (item) => isYesterday(item.lastActivity),
    },
    {
      label: this.translocoService.translate('Cette semaine'),
      condition: (item) => isThisWeek(item.lastActivity),
    },
    {
      label: this.translocoService.translate('Ce mois-ci'),
      condition: (item) => isThisMonth(item.lastActivity),
    },
    {
      label: this.translocoService.translate('Le mois dernier'),
      condition: (item) => isSameMonth(item.lastActivity, subMonths(new Date(), 1)),
    },
  ];

  getPatientSectionList(patients: Patient[], order?: SortOrderStrategy): SectionListData<Patient> {
    switch (order) {
      case SortOrderStrategy.ALPHABETICAL:
        return getSectionListData(patients, [], (patient) => this.alphabeticalPatientHandler(patient));
      case SortOrderStrategy.LAST_ACTIVITY:
        return getSectionListData(patients, this.dateSectionConfigs, (patient) => this.datePatientHandler(patient));
      default:
        return [{ label: null, data: patients }];
    }
  }

  private alphabeticalPatientHandler(patient: Patient): string {
    const firstLetter = patient.lastName?.charAt(0) ?? '';
    if (this.ALPHABETICAL_REGEX.test(firstLetter)) {
      return firstLetter.toUpperCase();
    }
    return '#';
  }

  private datePatientHandler(patient: Patient): string {
    const date = patient.lastActivity;
    if (!date) {
      return this.translocoService.translate('Plus ancien');
    }
    return capitalize(this.datePipe.transform(date, 'MMMM yyyy'));
  }
}
